import {
  showNotificationAction,
  fetchIssueAttributesDefinitionsAction,
  fetchIssueAttributesDefinitionsFinishedAction,
  fetchIssueAttributesMappingsFinishedAction,
  fetchIssueAttributesMappingsAction,
  trackEvent,
  ISSUE_ATTRIBUTES_FETCH_API,
  ISSUE_ATTRIBUTES_FETCH_START,
  ISSUE_ATTRIBUTES_CREATE_START,
  ISSUE_ATTRIBUTES_CREATE_API,
  ISSUE_ATTRIBUTES_CREATE_API_REJECTED,
  ISSUE_ATTRIBUTES_CREATE_API_FULFILLED,
  ISSUE_ATTRIBUTES_FETCH_API_FULFILLED,
  ISSUE_ATTRIBUTES_MAPPINGS_FETCH_START,
  ISSUE_ATTRIBUTES_MAPPINGS_FETCH_API_FULFILLED,
  ISSUE_ATTRIBUTES_MAPPINGS_FETCH_API,
  ISSUE_ATTRIBUTES_UPDATE_START,
  ISSUE_ATTRIBUTES_UPDATE_API,
  ISSUE_ATTRIBUTES_UPDATE_API_REJECTED,
  ISSUE_ATTRIBUTES_UPDATE_API_FULFILLED,
  ISSUE_ATTRIBUTES_DELETE_API,
  ISSUE_ATTRIBUTES_DELETE_START,
  ISSUE_ATTRIBUTES_DELETE_API_FULFILLED,
  ISSUE_ATTRIBUTES_DELETE_API_REJECTED,
  ISSUE_ATTRIBUTES_DEFINITIONS_FETCH_FINISHED,
  ISSUE_ATTRIBUTES_MAPPINGS_UPDATE_START,
  ISSUE_ATTRIBUTES_MAPPINGS_UPDATE_API,
  ISSUE_ATTRIBUTES_MAPPINGS_UPDATE_API_FULFILLED,
  ISSUE_TYPES_SET_ATTRIBUTE_MAPPINGS,
  ISSUE_ATTRIBUTES_MAPPINGS_SET
} from '../actions';
import i18n from '../../utils/i18n';
import {NotificationTypes, NotificationLevels} from '@adsk/bim360-matrix-react-notification-manager/es/helpers/consts';
import {paginateRequest} from '../../utils/middleware-helpers';
import {ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES} from '../../constants/issue-attributes';
import {
  isIssueTypesAdminListFetched,
  isIssueAttributesMappingsFetched,
  isLoadingIssueAttributesMappings,
  getContainerId,
  getIssueAttributesDefinitionIds,
  getIssueAttributesDefinitionsEntitiesMap,
  getIssueAttributesMappingEntitiesMap,
  getIssueAttributesMappingIds,
  getSelectedIssueTypeAttributes,
  getIssueTypesAdminEntitiesMap
} from '../selectors';
import {
  FIELD_TRACK_EVENT,
  issueAttributesDelete,
  issueAttributesCreate,
  issueAttributesUpdate,
  issueTypeMapCustomAttribute
} from '../../utils/analytics-helpers';
import {flattenIssueAttributesResults} from '../../utils/issueAttributes-helpers';

export default issuesV2Api => store => next => action => {
  const state = store.getState();
  // eslint-disable-next-line default-case
  switch (action.type) {
    case ISSUE_ATTRIBUTES_FETCH_START:
      store.dispatch({
        type: ISSUE_ATTRIBUTES_FETCH_API,
        payload: issuesV2Api.getIssueAttributeDefinitions(action.payload.offset)
      });
      break;
    case ISSUE_ATTRIBUTES_FETCH_API_FULFILLED:
      // Call next(action) in order to ensure the current action finishes. Only then perform another pagination request.
      next(action);
      paginateRequest(
        store,
        action,
        fetchIssueAttributesDefinitionsAction,
        fetchIssueAttributesDefinitionsFinishedAction
      );
      return;
    // For more info, see documentation of FIELD_ISSUES_FETCH_CUSTOMIZATION_METADATA
    case ISSUE_ATTRIBUTES_DEFINITIONS_FETCH_FINISHED:
      isIssueTypesAdminListFetched(state) &&
        !isIssueAttributesMappingsFetched(state) &&
        !isLoadingIssueAttributesMappings(state) &&
        store.dispatch(fetchIssueAttributesMappingsAction());
      break;
    case ISSUE_ATTRIBUTES_CREATE_START:
      store.dispatch({
        type: ISSUE_ATTRIBUTES_CREATE_API,
        payload: issuesV2Api.createIssueAttributeDefinition(action.payload),
        meta: {
          ...action.meta,
          title: action.payload.title,
          description: action.payload.description,
          dataType: action.payload.dataType
        }
      });
      break;
    case ISSUE_ATTRIBUTES_UPDATE_START:
      store.dispatch({
        type: ISSUE_ATTRIBUTES_UPDATE_API,
        payload: issuesV2Api.updateIssueAttributeDefinition(action.meta.id, action.payload),
        meta: {
          ...action.meta,
          descriptionEdited: !!action.payload.description,
          titleEdited: !!action.payload.title
        }
      });
      break;
    case ISSUE_ATTRIBUTES_DELETE_START:
      store.dispatch({
        type: ISSUE_ATTRIBUTES_DELETE_API,
        payload: issuesV2Api.deleteIssueAttributeDefinition(action.payload),
        meta: action.meta
      });
      break;
    case ISSUE_ATTRIBUTES_CREATE_API_REJECTED: {
      store.dispatch(
        showNotificationAction({
          type: NotificationTypes.SNACKBAR,
          level: NotificationLevels.ERROR,
          message: i18n.t('issueAttributesAdmin.notifications.createError', {
            issueAttributeName: action.meta.title
          })
        })
      );
      break;
    }
    case ISSUE_ATTRIBUTES_CREATE_API_FULFILLED: {
      const onSuccess = action.meta.onSuccess;
      onSuccess && onSuccess();

      store.dispatch(trackEvent(issueAttributesCreate(action.meta), FIELD_TRACK_EVENT));
      store.dispatch(
        showNotificationAction({
          type: NotificationTypes.TOAST,
          level: NotificationLevels.SUCCESS,
          message: i18n.t('issueAttributesAdmin.notifications.createSuccess', {
            issueAttributeName: action.meta.title
          })
        })
      );
      break;
    }
    case ISSUE_ATTRIBUTES_MAPPINGS_FETCH_START:
      store.dispatch({
        type: ISSUE_ATTRIBUTES_MAPPINGS_FETCH_API,
        payload: issuesV2Api.getIssueAttributeMappings(action.payload.offset)
      });
      break;
    case ISSUE_ATTRIBUTES_MAPPINGS_FETCH_API_FULFILLED:
      // Call next(action) in order to ensure the current action finishes. Only then perform another pagination request.
      next(action);
      paginateRequest(store, action, fetchIssueAttributesMappingsAction, fetchIssueAttributesMappingsFinishedAction);
      return;
    case ISSUE_ATTRIBUTES_UPDATE_API_REJECTED: {
      store.dispatch(
        showNotificationAction({
          type: NotificationTypes.SNACKBAR,
          level: NotificationLevels.ERROR,
          message: i18n.t('issueAttributesAdmin.notifications.updateError', {
            issueAttributeName: action.meta.title
          })
        })
      );
      break;
    }
    case ISSUE_ATTRIBUTES_UPDATE_API_FULFILLED: {
      const onSuccess = action.meta.onSuccess;
      onSuccess && onSuccess();
      store.dispatch(
        trackEvent(issueAttributesUpdate(action.meta.titleEdited, action.meta.descriptionEdited), FIELD_TRACK_EVENT)
      );
      store.dispatch(
        showNotificationAction({
          type: NotificationTypes.TOAST,
          level: NotificationLevels.SUCCESS,
          message: i18n.t('issueAttributesAdmin.notifications.updateSuccess', {
            issueAttributeName: action.meta.title
          })
        })
      );
      break;
    }
    case ISSUE_ATTRIBUTES_DELETE_API_FULFILLED: {
      let entitiesMap = getIssueAttributesDefinitionsEntitiesMap(state);
      entitiesMap = entitiesMap.without(action.meta.id);
      let issueAttributeIds = getIssueAttributesDefinitionIds(state);
      issueAttributeIds = issueAttributeIds.filter(issueAttributeId => issueAttributeId !== action.meta.id);
      action.payload = {
        entitiesMap,
        issueAttributeIds
      };

      const mappingIds = getIssueAttributesMappingIds(state);
      const mappingEntitiesMap = getIssueAttributesMappingEntitiesMap(state);
      const mappingIdsWithoutDeleted = mappingIds
        .map(id => mappingEntitiesMap[id])
        .filter(mapping => mapping.attributeDefinitionId !== action.meta.id)
        .map(mapping => mapping.id);
      const mappingEntitiesMapWithoutDeleted = mappingEntitiesMap.without(
        attribute => attribute.attributeDefinitionId === action.meta.id
      );

      store.dispatch({
        type: ISSUE_ATTRIBUTES_MAPPINGS_SET,
        payload: {
          mappingIds: mappingIdsWithoutDeleted,
          entitiesMap: mappingEntitiesMapWithoutDeleted
        }
      });

      store.dispatch({
        type: ISSUE_TYPES_SET_ATTRIBUTE_MAPPINGS,
        payload: mappingIdsWithoutDeleted.map(id => mappingEntitiesMapWithoutDeleted[id]).asMutable(),
        meta: {containerId: getContainerId(state)}
      });

      const onComplete = action.meta.onComplete;
      onComplete && onComplete();
      store.dispatch(trackEvent(issueAttributesDelete(), FIELD_TRACK_EVENT));
      store.dispatch(
        showNotificationAction({
          type: NotificationTypes.TOAST,
          level: NotificationLevels.SUCCESS,
          message: i18n.t('issueAttributesAdmin.notifications.deleteSuccess', {
            issueAttributeName: action.meta.title
          })
        })
      );
      break;
    }
    case ISSUE_ATTRIBUTES_DELETE_API_REJECTED: {
      const onComplete = action.meta.onComplete;
      onComplete && onComplete();
      store.dispatch(
        showNotificationAction({
          type: NotificationTypes.SNACKBAR,
          level: NotificationLevels.ERROR,
          message: i18n.t('issueAttributesAdmin.notifications.deleteError', {
            issueAttributeName: action.meta.title
          })
        })
      );
      break;
    }
    case ISSUE_ATTRIBUTES_MAPPINGS_UPDATE_START:
      {
        const {id, parentId, isContainerType, onSuccess} = action.payload;

        const newAttributesToUpdate = getSelectedIssueTypeAttributes(state).map(mapping => ({
          attributeDefinitionId: mapping.attributeDefinitionId,
          isRequired: mapping.isRequired
        }));
        const {CONTAINER, SUBTYPE, TYPE} = ISSUE_ATTRIBUTES_MAPPED_ITEM_TYPES;
        const mappedItemType = isContainerType ? CONTAINER : parentId ? SUBTYPE : TYPE;

        store.dispatch({
          type: ISSUE_ATTRIBUTES_MAPPINGS_UPDATE_API,
          payload: issuesV2Api.updateIssueTypeMappings({
            mappedItemId: id,
            mappedItemType,
            attributeDefinitions: newAttributesToUpdate
          }),
          meta: {
            onSuccess,
            id,
            parentId,
            mappedItemType,
            numOfAddedAttributes: newAttributesToUpdate.length
          }
        });
      }
      break;

    case ISSUE_ATTRIBUTES_MAPPINGS_UPDATE_API_FULFILLED: {
      const mappingIds = getIssueAttributesMappingIds(state);
      const entitiesMap = getIssueAttributesMappingEntitiesMap(state);
      const mappedItemId = action.meta.id;
      const results = action.payload;
      const attributeIdsWithoutCurrent = mappingIds
        .map(id => entitiesMap[id])
        .filter(mapping => mapping.mappedItemId !== mappedItemId)
        .map(mapping => mapping.id);

      const {ids, entitiesMap: newEntitiesMap} = flattenIssueAttributesResults(results);

      const mergedEntitiesMap = entitiesMap
        .without(attribute => attribute.mappedItemId === mappedItemId)
        .merge(newEntitiesMap);
      const mergedMappingIds = attributeIdsWithoutCurrent.concat(ids);
      action.payload = {
        mappingIds: mergedMappingIds,
        entitiesMap: mergedEntitiesMap
      };

      next(action);
      store.dispatch({
        type: ISSUE_TYPES_SET_ATTRIBUTE_MAPPINGS,
        payload: mergedMappingIds.map(id => mergedEntitiesMap[id]).asMutable(),
        meta: {containerId: getContainerId(state)}
      });
      action.meta.onSuccess && action.meta.onSuccess();

      const issueTypesMap = getIssueTypesAdminEntitiesMap(state);
      const mappedItemType = action.meta.mappedItemType;
      const itemName = issueTypesMap[mappedItemId].title;
      const totalNumberOfAttributes = action.meta.numOfAddedAttributes;
      store.dispatch(trackEvent(issueTypeMapCustomAttribute(mappedItemType, itemName, totalNumberOfAttributes)));

      break;
    }
  }
  // eslint-disable-next-line consistent-return
  return next(action);
};
